import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';

const ResponsiveTable = ({ 
  data = [], 
  columns = [],
  loading = false,
  emptyMessage = 'Δεν βρέθηκαν αποτελέσματα',
  searchTerm = '',
  sortField = null,
  sortDirection = 'asc',
  onSort = null,
  onRowClick = null,
  renderMobileCard = null,
  stickyHeader = false,
  maxHeight = null,
  serverSide = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Handle sort click
  const handleSort = (field, columnSortField = null) => {
    if (!onSort || !field) return;
    
    // Use column's sortField if provided, otherwise use the field
    const actualSortField = columnSortField || field;
    const newDirection = sortField === actualSortField && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(actualSortField, newDirection);
  };

  // Filter data for client-side filtering (when serverSide is false)
  const filteredData = React.useMemo(() => {
    if (serverSide || !searchTerm) return data;
    
    return data.filter(row => 
      columns.some(column => {
        const field = column.field || column.id;
        const value = row[field];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns, serverSide]);

  // Sort data for client-side sorting (when serverSide is false)
  const sortedData = React.useMemo(() => {
    if (serverSide || !sortField) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      // Find column to determine field name
      const sortColumn = columns.find(col => (col.field || col.id) === sortField);
      const actualField = sortColumn?.field || sortColumn?.id || sortField;
      
      const aValue = a[actualField] || '';
      const bValue = b[actualField] || '';
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredData, sortField, sortDirection, serverSide]);

  const finalData = sortedData;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '200px'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Empty state
  if (!finalData || finalData.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  // Mobile view with cards
  if (isMobile && renderMobileCard) {
    return (
      <Stack spacing={2}>
        {finalData.map((row, index) => renderMobileCard(row, index))}
      </Stack>
    );
  }

  // Desktop table view
  return (
    <TableContainer 
      component={Paper}
      sx={{ 
        maxHeight: maxHeight,
        '& .MuiTableCell-head': {
          backgroundColor: theme.palette.grey[50],
          fontWeight: 600
        }
      }}
    >
      <Table stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const field = column.field || column.id;
              const label = column.headerName || column.label;
              
              return (
                <TableCell 
                  key={field || column.id}
                  align={column.align || 'left'}
                  sx={{ 
                    minWidth: column.minWidth,
                    width: column.width
                  }}
                >
                  {column.sortable && onSort ? (
                    <TableSortLabel
                      active={sortField === (column.sortField || field)}
                      direction={sortField === (column.sortField || field) ? sortDirection : 'asc'}
                      onClick={() => handleSort(field, column.sortField)}
                      sx={{ fontWeight: 600 }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {finalData.map((row, index) => (
            <TableRow
              key={index}
              hover={Boolean(onRowClick)}
              onClick={() => onRowClick && onRowClick(row)}
              sx={{ 
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {columns.map((column, colIndex) => {
                const field = column.field || column.id;
                const renderFunction = column.renderCell || column.render;
                // Check if column uses 'render' (NotificationListingPage style) vs 'renderCell' (other components)
                const usesRenderOnly = !!column.render && !column.renderCell;
                
                // Get cell content
                let cellContent;
                if (renderFunction && typeof renderFunction === 'function') {
                  try {
                    let rendered;
                    
                    // If using 'render' only (NotificationListingPage pattern), always pass just the row
                    if (usesRenderOnly) {
                      rendered = renderFunction(row);
                    } else {
                      // Otherwise, try both signatures for backward compatibility
                      const fieldValue = field ? row[field] : undefined;
                      try {
                        // First try with (value, row) signature - most common in other components
                        rendered = renderFunction(fieldValue, row);
                      } catch (e1) {
                        try {
                          // If that fails, try with just (row)
                          rendered = renderFunction(row);
                        } catch (e2) {
                          // If both fail, log and use fallback
                          console.error('Error calling renderFunction for column', field || column.id, e2);
                          rendered = null;
                        }
                      }
                    }
                    
                    // Validate that rendered value is valid for React
                    if (rendered != null && rendered !== undefined) {
                      // Check if it's a valid React element
                      if (React.isValidElement(rendered)) {
                        cellContent = rendered;
                      } 
                      // Check if it's a primitive (string, number, boolean)
                      else if (typeof rendered !== 'object') {
                        cellContent = rendered;
                      }
                      // It's an object - try to convert it safely
                      else if (Array.isArray(rendered)) {
                        // Array - join elements
                        cellContent = rendered.length > 0 ? rendered.join(', ') : null;
                      } else {
                        // Plain object - try to get meaningful string
                        const strValue = rendered.name || rendered.title || rendered.label || rendered.toString?.() || JSON.stringify(rendered);
                        const finalStr = String(strValue);
                        cellContent = finalStr.length > 100 ? finalStr.substring(0, 100) + '...' : finalStr;
                        console.warn(`Column '${field || column.id || colIndex}' render returned object, converted to: ${finalStr.substring(0, 50)}...`);
                      }
                    } else {
                      cellContent = (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      );
                    }
                  } catch (error) {
                    console.error('Error in renderCell for column', field || column.id || colIndex, error);
                    cellContent = (
                      <Typography variant="body2" color="error">
                        Error
                      </Typography>
                    );
                  }
                } else {
                  // No renderCell/render function - get raw value from row
                  const value = field ? row[field] : null;
                  
                  if (value == null) {
                    cellContent = (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    );
                  } else if (React.isValidElement(value)) {
                    // Already a React element - use it directly
                    cellContent = value;
                  } else if (typeof value === 'object') {
                    // Object value - convert to string
                    if (Array.isArray(value)) {
                      const arrayStr = value.length > 0 ? value.join(', ') : '-';
                      cellContent = (
                        <Typography variant="body2" color={value.length === 0 ? 'text.secondary' : undefined}>
                          {arrayStr}
                        </Typography>
                      );
                    } else {
                      // Try to get meaningful string from object
                      const strValue = value.name || value.title || value.label || JSON.stringify(value);
                      const finalStr = String(strValue).length > 100 
                        ? String(strValue).substring(0, 100) + '...' 
                        : String(strValue);
                      cellContent = (
                        <Typography variant="body2">
                          {finalStr}
                        </Typography>
                      );
                    }
                  } else {
                    // Primitive value - wrap in Typography for consistency
                    cellContent = (
                      <Typography variant="body2">
                        {String(value)}
                      </Typography>
                    );
                  }
                }
                
                return (
                  <TableCell 
                    key={field || column.id || `col-${colIndex}`}
                    align={column.align || 'left'}
                  >
                    {cellContent}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResponsiveTable;